import { cn } from 'noddi-ui-common';
import { MouseEvent, ReactNode } from 'react';
import { calendarEntryVariants, calendarStatusDotVariants } from './calendarEntryVariants';
import { CalendarEntryStatus } from './types';

interface Props {
  status: CalendarEntryStatus;
  /** `HH:mm`. Omit for an entry with no time of its own — nothing is invented in its place. */
  time?: string;
  /** The entry's identity: a license plate, a name, a title. */
  label: string;
  /** Second line, when the label alone does not say whose entry this is. */
  sublabel?: string | null;
  /** Native tooltip, for the full text of an entry a cell has to truncate. */
  title?: string;
  strikethrough?: boolean;
  /** Given, the entry is its own button; otherwise it is inert and the day cell owns the click. */
  onClick?: (event: MouseEvent) => void;
}

export const CalendarEntry = ({ status, time, label, sublabel, title, strikethrough, onClick }: Props) => {
  const body: ReactNode = (
    <>
      <span className='flex min-w-0 items-center gap-1.5'>
        <span aria-hidden className={calendarStatusDotVariants({ status })} />
        <span className='flex min-w-0 items-center gap-1'>
          {time && <span className='shrink-0 tabular-nums'>{time}</span>}
          <span className={cn('truncate font-semibold', strikethrough && 'line-through')}>{label}</span>
        </span>
      </span>
      {sublabel && <span className='truncate text-[10px] leading-tight text-muted-foreground'>{sublabel}</span>}
    </>
  );

  if (!onClick) {
    return (
      <div className={calendarEntryVariants({ status })} title={title}>
        {body}
      </div>
    );
  }

  return (
    <button type='button' onClick={onClick} className={calendarEntryVariants({ status })} title={title}>
      {body}
    </button>
  );
};
